<template>
  <span :class="badgeClasses">
    <!-- Dot indicator -->
    <span v-if="dot" :class="dotClasses"></span>

    <!-- Icon (left) -->
    <span v-if="iconLeft" class="mr-1">
      <slot name="icon-left">{{ iconLeft }}</slot>
    </span>

    <!-- Badge content -->
    <slot>{{ text }}</slot>

    <!-- Icon (right) -->
    <span v-if="iconRight" class="ml-1">
      <slot name="icon-right">{{ iconRight }}</slot>
    </span>

    <!-- Close button -->
    <button
      v-if="closable"
      type="button"
      class="ml-1.5 -mr-0.5 hover:opacity-70 focus:outline-none"
      @click="handleClose"
    >
      <i class="fas fa-times text-[10px]"></i>
    </button>
  </span>
</template>

<script>
export default {
  name: 'BaseBadge',
  props: {
    text: {
      type: [String, Number],
      default: null
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => [
        'primary',
        'secondary',
        'success',
        'danger',
        'warning',
        'info',
        'gray',
        'dark'
      ].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value)
    },
    rounded: {
      type: String,
      default: 'full',
      validator: (value) => ['sm', 'md', 'lg', 'full'].includes(value)
    },
    outline: {
      type: Boolean,
      default: false
    },
    dot: {
      type: Boolean,
      default: false
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    closable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    badgeClasses() {
      return [
        'inline-flex items-center font-medium',
        this.sizeClasses,
        this.variantClasses,
        this.roundedClasses
      ]
    },
    sizeClasses() {
      const sizes = {
        xs: 'px-1.5 py-0.5 text-[10px]',
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm'
      }
      return sizes[this.size]
    },
    variantClasses() {
      if (this.outline) {
        const outlineVariants = {
          primary: 'bg-transparent text-primary border border-primary/30',
          secondary: 'bg-transparent text-secondary border border-secondary/30',
          success: 'bg-transparent text-green-600 border border-green-300',
          danger: 'bg-transparent text-red-600 border border-red-300',
          warning: 'bg-transparent text-yellow-600 border border-yellow-300',
          info: 'bg-transparent text-blue-600 border border-blue-300',
          gray: 'bg-transparent text-gray-600 border border-gray-300',
          dark: 'bg-transparent text-gray-800 border border-gray-400'
        }
        return outlineVariants[this.variant]
      }

      // Pastel/soft colors for default badges
      const variants = {
        primary: 'bg-primary-100 text-primary-dark',
        secondary: 'bg-green-100 text-green-700',
        success: 'bg-green-100 text-green-700',
        danger: 'bg-red-100 text-red-700',
        warning: 'bg-yellow-100 text-yellow-800',
        info: 'bg-blue-100 text-blue-700',
        gray: 'bg-gray-100 text-gray-600',
        dark: 'bg-gray-800 text-white'
      }
      return variants[this.variant]
    },
    roundedClasses() {
      const rounded = {
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full'
      }
      return rounded[this.rounded]
    },
    dotClasses() {
      const dotColors = {
        primary: 'bg-primary',
        secondary: 'bg-secondary',
        success: 'bg-green-500',
        danger: 'bg-red-500',
        warning: 'bg-yellow-500',
        info: 'bg-blue-500',
        gray: 'bg-gray-400',
        dark: 'bg-gray-700'
      }
      return ['w-1.5 h-1.5 rounded-full mr-1.5', dotColors[this.variant]]
    }
  },
  methods: {
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>
